import qs from 'qs';
import { useMutation, useQuery } from 'react-query';
import { network } from 'services/network';

import { CriticalPathSettingOptions } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/CriticalPath/criticalPath';
import { TDefaultOverdueAndUrgentDateThreshold } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/SubProgramPreferences/subProgramPreferences.utils';

interface OverdueAndUrgentDateThreshold {
    subProgramId?: number;
    option?: string;
}

interface SetTagList {
    contractId: number | undefined;
    tagList: SelectListWithCreateData[];
}

interface IGetSubProgramPreferenceCriticalPathCompareValueResponse {
    defaultCriticalPathSetting: CriticalPathSettingOptions;
    defaultCopmareDays: number;
}

interface ISetSubProgramPreferenceCriticalPathCompareValuePayload {
    projectId: number;
    subProgramId: number;
    option: CriticalPathSettingOptions;
    value: number;
    isNeedRefresh: boolean;
}

interface IGetSubProgramPreferenceImportanceResponse {
    defaultCriticalPathSetting: boolean;
    defaultTopCriticalityPercent: number;
    defaultCompletionPercent: number;
}

interface ISetSubProgramPreferenceImportancePayload {
    projectId: number;
    subProgramId: number;
    topCriticalityPercent: number;
    completionPercent: number;
    isNeedRefresh: boolean;
}

const getOverdueAndUrgentDateThreshold = (subProgramId) => {
    return network
        .get(`api/bo/getSubProgramPreferenceToggleDate?subProgramId=${subProgramId}`)
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const setOverdueAndUrgentDateThreshold = ({ subProgramId, option }: OverdueAndUrgentDateThreshold) => {
    const querystring = qs.stringify({ subProgramId, option }, { addQueryPrefix: true, skipNulls: true });
    const url = `api/bo/setSubProgramPreferenceToggleDate${querystring}`;
    return network
        .post(url)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const setSubProgramPreferenceCriticalPathCompareValue = ({ projectId, subProgramId, option, value, isNeedRefresh }) => {
    const querystring = qs.stringify(
        { projectId, subProgramId, option, value, isNeedRefresh },
        { addQueryPrefix: true, skipNulls: true },
    );
    const url = `api/bo/setSubProgramPreferenceCriticalPathCompareValue${querystring}`;

    return network
        .post(url)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const setSubProgramPreferenceImportance = ({
    projectId,
    subProgramId,
    topCriticalityPercent,
    completionPercent,
    isNeedRefresh,
}) => {
    const querystring = qs.stringify(
        { projectId, subProgramId, topCriticalityPercent, completionPercent, isNeedRefresh },
        { addQueryPrefix: true, skipNulls: true },
    );
    const url = `api/bo/setSubProgramPreferenceImportance${querystring}`;

    return network
        .post(url)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const getTagList = (contractId) => {
    return network
        .get(`api/bo/management/getContract?contractId=${contractId}`)
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const setTagList = (data) => {
    return network
        .post('api/bo/management/updateContract', data)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const getSubProgramPreferenceInfo = () => {
    return network
        .get('api/bo/getSubProgramPreferenceInfo')
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const getSubProgramPreferenceCriticalPathCompareValue = ({ subProgramId }) => {
    const querystring = qs.stringify({ subProgramId }, { addQueryPrefix: true, skipNulls: true });
    const url = `api/bo/getSubProgramPreferenceCriticalPathCompareValue${querystring}`;
    return network
        .post(url)
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const getSubProgramPreferenceImportance = ({ subProgramId }) => {
    const querystring = qs.stringify({ subProgramId }, { addQueryPrefix: true, skipNulls: true });
    const url = `api/bo/getSubProgramPreferenceImportance${querystring}`;
    return network
        .get(url)
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

export const useQueryOverdueAndUrgentDateThreshold = ({ subProgramId }) => {
    return useQuery<TDefaultOverdueAndUrgentDateThreshold, QueryError>(
        ['useQueryOverdueAndUrgentDateThreshold', subProgramId],
        () => getOverdueAndUrgentDateThreshold(subProgramId),
        {
            enabled: Boolean(subProgramId),
        },
    );
};

export const useMutationSetOverdueAndUrgentDateThreshold = () => {
    return useMutation<any, QueryError, OverdueAndUrgentDateThreshold>((params) =>
        setOverdueAndUrgentDateThreshold(params),
    );
};

export const useQueryGetTagList = ({ contractId }) => {
    return useQuery(['useQueryGetTagList', contractId], () => getTagList(contractId), {
        enabled: Boolean(contractId),
    });
};

export const useMutationSetTagList = () => {
    return useMutation<any, QueryError, SetTagList>((params) => setTagList(params));
};

export const useQueryGetSubProgramPreferenceInfo = () => {
    return useQuery(['useQueryGetSubProgramPreferenceInfo'], () => getSubProgramPreferenceInfo());
};

export const useQueryGetSubProgramPreferenceCriticalPathCompareValue = ({ subProgramId }) => {
    return useQuery<IGetSubProgramPreferenceCriticalPathCompareValueResponse, QueryError>(
        ['getSubProgramPreferenceCriticalPathCompareValue', subProgramId],
        () => getSubProgramPreferenceCriticalPathCompareValue({ subProgramId }),
        {
            enabled: Boolean(subProgramId),
        },
    );
};

export const useMutationSetSubProgramPreferenceCriticalPathCompareValue = () => {
    return useMutation<string, QueryError, ISetSubProgramPreferenceCriticalPathCompareValuePayload>((params) =>
        setSubProgramPreferenceCriticalPathCompareValue(params),
    );
};

export const useQueryGetSubProgramPreferenceImportance = ({ subProgramId }) => {
    return useQuery<IGetSubProgramPreferenceImportanceResponse, QueryError>(
        ['getSubProgramPreferenceImportance', subProgramId],
        () => getSubProgramPreferenceImportance({ subProgramId }),
        {
            enabled: Boolean(subProgramId),
        },
    );
};

export const useMutationSetSubProgramPreferenceImportance = () => {
    return useMutation<string, QueryError, ISetSubProgramPreferenceImportancePayload>((params) =>
        setSubProgramPreferenceImportance(params),
    );
};
